import { DateRange } from '@mui/x-date-pickers-pro'
import dayjs from 'dayjs'

import SETTINGS from 'src/settings'
import 'temporal-polyfill/global'

import { GranularityEnum } from './types'

export const TIMEZONE_KEY = 'plato:timezone'

export interface ITemporalRange {
  from?: Temporal.ZonedDateTime
  to?: Temporal.ZonedDateTime
}

export function normalizeFrom(from: Temporal.ZonedDateTime, approximation: GranularityEnum): Temporal.ZonedDateTime {
  switch (approximation) {
    case GranularityEnum.hour:
      const hour: number = from.hour
      return from.startOfDay().add({ hours: hour })
    case GranularityEnum.day:
      return from.startOfDay()
    case GranularityEnum.week:
      return from.startOfDay().subtract({ days: from.dayOfWeek - 1 })
    case GranularityEnum.month:
      return from.startOfDay().subtract({ days: from.day - 1 })
    case GranularityEnum.quarter:
      if (from.month % 3 === 1) {
        return from.startOfDay().subtract({ days: from.day - 1 })
      }
      return normalizeFrom(from.subtract({ months: 1 }), approximation)
    default:
      return from.startOfDay()
  }
}

export function normalizeTo(to: Temporal.ZonedDateTime): Temporal.ZonedDateTime {
  return to.startOfDay().add(Temporal.Duration.from({ hours: 23, minutes: 59, seconds: 59 }))
}

export function addDay(t: Temporal.ZonedDateTime): Temporal.ZonedDateTime {
  return t.add({ days: 1 })
}

export function getTemporalDurationLike(granularity: GranularityEnum, amount: number): Temporal.DurationLike {
  switch (granularity) {
    case GranularityEnum.hour:
      return { hours: amount }
    case GranularityEnum.day:
      return { days: amount }
    case GranularityEnum.week:
      return { weeks: amount }
    case GranularityEnum.month:
      return { months: amount }
    default:
      return { days: amount }
  }
}

export function saveJurisdictionTimezone(timezone: string): void {
  localStorage.setItem(TIMEZONE_KEY, timezone)
}

export function getJurisdictionTimezone(): string {
  return localStorage.getItem(TIMEZONE_KEY) || SETTINGS.app.timezone
}

export function getTemporalRange(
  from: Temporal.DurationLike,
  normalize = false,
  approximation: GranularityEnum = GranularityEnum.day
): ITemporalRange {
  const range: ITemporalRange = {
    from: Temporal.Now.instant().toZonedDateTimeISO(getJurisdictionTimezone()).subtract(Temporal.Duration.from(from)),
    to: Temporal.Now.instant().toZonedDateTimeISO(getJurisdictionTimezone()),
  }
  if (normalize && range.from && range.to) {
    range.from = normalizeFrom(range.from, approximation)
    range.to = normalizeTo(range.to)
  }
  return range
}

export const zonedDateTimeToISOString = (t: Temporal.ZonedDateTime | string): string => {
  try {
    return t.toString({ timeZoneName: 'never' })
  } catch {
    return t.toString()
  }
}

export const JSONStringify = (value: any) => {
  return JSON.stringify(value, (_: string, value: any) => {
    try {
      if (value.includes(tzid())) {
        return value.replace(tzid(), '')
      }
    } catch {}
    return value
  })
}

/**
 * Parses an ISO 8601 string (typically what's returned by our API).
 * Returns a Temporal ZonedDateTime.
 * Note that Temporal.ZonedDateTime.from() rejects ISO 8601 strings, which do not include a time zone identifier. This is why we first convert the ISO string to a Temporal Instant.
 * see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Temporal/ZonedDateTime/from#creating_a_zoneddatetime_from_an_iso_8601_rfc_3339_string
 */
export const stringToZonedDateTime = (isoString: string): Temporal.ZonedDateTime => {
  return Temporal.Instant.from(isoString).toZonedDateTimeISO(getJurisdictionTimezone())
}

export const epochToZonedDateTime = (epoch: number): Temporal.ZonedDateTime => {
  return Temporal.Instant.fromEpochMilliseconds(epoch).toZonedDateTimeISO(getJurisdictionTimezone())
}

export const TemporalOrNow = (t: Temporal.ZonedDateTime): Temporal.ZonedDateTime => {
  const instant = TemporalNow()
  if (instant.epochMilliseconds < t.epochMilliseconds) {
    return instant
  }
  return t
}

export const TemporalNow = (): Temporal.ZonedDateTime =>
  Temporal.Now.instant().toZonedDateTimeISO(getJurisdictionTimezone())

export const is_same_date = (d1: DateRange<dayjs.Dayjs>, d2: DateRange<dayjs.Dayjs>): boolean => {
  if (
    d1[0]?.year() == d2[0]?.year() &&
    d1[0]?.month() == d2[0]?.month() &&
    d1[0]?.date() == d2[0]?.date() &&
    d1[1]?.year() == d2[1]?.year() &&
    d1[1]?.month() == d2[1]?.month() &&
    d1[1]?.date() == d2[1]?.date()
  ) {
    return true
  }
  return false
}

export const get_utc_offset = (t: Temporal.ZonedDateTime) => {
  return t.offsetNanoseconds / 60000000000
}

export const add_offset = (t: Temporal.ZonedDateTime): Temporal.ZonedDateTime => {
  return t.add(
    Temporal.Duration.from({
      minutes: -get_utc_offset(t) + dayjs(zonedDateTimeToISOString(t)).tz(getJurisdictionTimezone()).utcOffset(),
    })
  )
}

export const substract_offset = (t: Temporal.ZonedDateTime): Temporal.ZonedDateTime => {
  return t.subtract(
    Temporal.Duration.from({
      minutes: -get_utc_offset(t) + dayjs(zonedDateTimeToISOString(t)).tz(getJurisdictionTimezone()).utcOffset(),
    })
  )
}

const tzid = () => `[${Temporal.Now.instant().toZonedDateTimeISO(getJurisdictionTimezone()).timeZoneId}]`

export const temporalEqual = (t1?: Temporal.ZonedDateTime, t2?: Temporal.ZonedDateTime): boolean => {
  if (t1 === undefined || t2 === undefined) {
    return false
  }
  return t1.epochMilliseconds === t2.epochMilliseconds
}

export const removeSeconds = (t: Temporal.ZonedDateTime) => {
  return t.subtract({ seconds: t.second })
}

export const humanizedDuration = (t: Temporal.Duration, suffix = true): string => {
  const ISO8601Duration = t.toString()
  const duration = dayjs.duration(ISO8601Duration)
  if (ISO8601Duration.startsWith('-')) {
    // https://github.com/iamkun/dayjs/pull/1853
    // @ts-expect-error There’s a bug lying in dayjs duration.
    // It simply doesn’t handle
    // negative duration (i.e. instead of "A month ago" you will receive "In a month")
    // Hence this hacky thing
    duration.$ms = -duration.$ms
  }
  return duration.humanize(suffix)
}

export const dateToDayJSLocalized = (d: string): dayjs.Dayjs => {
  return dayjs(d).tz(getJurisdictionTimezone())
}

export const isAfter = (firstDuration: Temporal.ZonedDateTime, secondDuration: Temporal.ZonedDateTime): number => {
  return firstDuration.since(secondDuration).sign
}

export const isBefore = (firstDuration: Temporal.ZonedDateTime, secondDuration: Temporal.ZonedDateTime): number => {
  return firstDuration.until(secondDuration).sign
}

export function temporalToDayJS(value: Temporal.ZonedDateTime): dayjs.Dayjs
export function temporalToDayJS(value: Temporal.ZonedDateTime | undefined): dayjs.Dayjs | null
export function temporalToDayJS(value: Temporal.ZonedDateTime | undefined): dayjs.Dayjs | null {
  if (!value) {
    return null
  }
  return dayjs(zonedDateTimeToISOString(value)).tz(getJurisdictionTimezone())
}
